// ./src/store/actions/checkActions.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { readCheckScope as checkContext } from './checkScope';

async function checkFetch(url, options = {}, context = checkContext()) {
  const headers = { ...options.headers, Authorization: `Bearer ${localStorage.getItem('token')}` };
  if (options.body instanceof FormData && url.endsWith('/submit')) {
    const copy = new FormData(); options.body.forEach((value, key) => copy.append(key, value));
    copy.set('locationCode', context.locationCode); options = { ...options, body: copy };
  }
  if (context.locationCode !== 'MAIN' && (!options.method || options.method === 'GET')) url += (url.includes('?') ? '&' : '?') + new URLSearchParams({ locationCode: context.locationCode }).toString();
  const response = await fetch(url, { ...options, headers });
  if (response.ok && options.method === 'POST' && context.scheduledCheckId) window.dispatchEvent(new Event('scheduled-check-updated'));
  return response;
}
const BASE_URL = `${process.env.REACT_APP_API_URL}/checks`;

// ============================================================
// FETCH CHECKLIST BY SLUG
// ============================================================

export const fetchChecklistBySlug = createAsyncThunk(
  "checklist/fetchBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      const response = await checkFetch(`${BASE_URL}/${slug}`);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch checklist template");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Network error occurred");
    }
  },
);

// ============================================================
// FETCH ALL CHECKLISTS
// ============================================================

export const fetchAllChecklists = createAsyncThunk(
  "checklist/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await checkFetch(`${BASE_URL}`);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Failed to fetch checklists"
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Network error occurred while fetching checklists"
      );
    }
  },
);

// ============================================================
// FETCH SUBMISSION BY DATE
// ============================================================

const fetchSubmissionByDateRequest = createAsyncThunk(
  "checklist/fetchSubmissionByDate",
  async ({ checklistId, creationDate, locationCode, scheduledCheckId }, { rejectWithValue }) => {
    try {
      const response = await checkFetch(
        `${BASE_URL}/submission/${checklistId}/${creationDate}`, {}, { locationCode, scheduledCheckId },
      );

      let data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to check submission records.");
      if (!data.exists && scheduledCheckId) {
        const form = new FormData(); form.append('checklistId', checklistId); form.append('creationDate', creationDate);
        const created = await checkFetch(`${BASE_URL}/submit`, { method: 'POST', body: form }, { locationCode, scheduledCheckId });
        const result = await created.json();
        if (!created.ok) throw new Error(result.message || 'Unable to open check.');
        data = { ...result, exists: true };
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Network error occurred checking records.",
      );
    }
  },
);

// ============================================================
// SUBMIT / UPDATE MAIN CHECKLIST DATA
// ============================================================

export function fetchSubmissionByDate(argument) { return fetchSubmissionByDateRequest({ ...checkContext(), ...argument, locationCode: argument.locationCode || checkContext().locationCode }); }
Object.assign(fetchSubmissionByDate, fetchSubmissionByDateRequest);

export const submitChecklistData = createAsyncThunk(
  "checklist/submitData",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await checkFetch(`${BASE_URL}/submit`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit checklist");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Network error occurred");
    }
  },
);

// ============================================================
// SUBMIT / UPDATE CHECKLIST ITEM RESULT
// ============================================================

export const submitChecklistResult = createAsyncThunk(
  "checklist/submitResult",
  async (payload, { rejectWithValue }) => {
    try {
      const isFormData = payload instanceof FormData;

      const response = await checkFetch(`${BASE_URL}/submit-result`, {
        method: "POST",
        ...(isFormData
          ? {
              body: payload,
            }
          : {
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.message ||
            "Failed to synchronize checklist result"
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Network error occurred during real-time sync"
      );
    }
  }
);

// ============================================================
// SUBMIT / UPDATE BULLET RESULT
// ============================================================
// Accepts FormData:
//
// submissionId
// checklistItemId
// bulletId
// isChecked       (optional)
// photo           (optional)
//
// Both isChecked and photo are independently optional.
// ============================================================

export const submitBulletResults = createAsyncThunk(
  "checklist/submitBulletResults",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await checkFetch(`${BASE_URL}/submit-bullet-results`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to synchronize bullet result",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Network error occurred during bullet sync",
      );
    }
  },
);

// ============================================================
// SUBMIT TEMPERATURE RESULT
// ============================================================

export const submitTemperatureResults = createAsyncThunk(
  "checklist/submitTemperatureResults",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await checkFetch(`${BASE_URL}/submit-temperature-results`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to submit temperature result",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message ||
          "Network error occurred while submitting temperature result",
      );
    }
  },
);

export const fetchChecklistStats = createAsyncThunk(
  "checklist/fetchStats",
  async (creationDate, { rejectWithValue, signal }) => {
    try {
      const response = await checkFetch(`${BASE_URL}/stats/${creationDate}`, { signal });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch checklist statistics");
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message ||
          "Network error occurred while fetching checklist statistics",
      );
    }
  },
);

// ============================================================
// FETCH CHECKLIST DETAIL / SUMMARY BY SLUG AND DATE
// ============================================================
// Returns:
// - totalChecklistItems
// - completedChecklistItems
// - passed
// - issued
// - pending
// - completionPercentage
//
// Bullet results are NOT included.
// ============================================================

export const fetchChecklistDetail = createAsyncThunk(
  "checklist/fetchDetail",
  async ({ slug, creationDate }, { rejectWithValue }) => {
    try {
      const response = await checkFetch(
        `${BASE_URL}/detail/${slug}/${creationDate}`,
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch checklist detail");
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message ||
          "Network error occurred while fetching checklist detail",
      );
    }
  },
);
